import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import PageStructuredData from "@/components/PageStructuredData";
import { SubpageNav, SubpageBackLink, SubpageFooter } from "@/components/SubpageLayout";
import SubpageHero from "@/components/SubpageHero";
import { HelpCircle, ChevronDown, Loader2, Search, MessageCircle, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TX = {
  bg: {
    eyebrow: "Помощен център",
    title: "Въпроси, които получаваме най-често",
    subtitle:
      "Събрали сме отговорите, които най-често даваме в поддръжката. Ако Вашият въпрос липсва — пишете ни, отговаряме лично.",
    meta: ["Образователна платформа", "Без медицинска диагноза", "Отговор до 24 часа"],
    search: 'Търсете по дума — например „изследвания“ или „абонамент“',
    empty: "Няма намерени въпроси по това търсене.",
    none: "В момента няма налични въпроси.",
    cta_title: "Не намерихте отговор?",
    cta_p: "Напишете ни въпроса си — отговаря човек от екипа, не автоматично съобщение.",
    cta_btn: "Задайте въпрос",
    guide_btn: "Отворете ръководството",
    back: "Начало",
  },
  en: {
    eyebrow: "Help centre",
    title: "The questions we're asked most",
    subtitle:
      "These are the answers we give most often in support. If your question isn't here, write to us — we reply personally.",
    meta: ["Educational platform", "Not a medical diagnosis", "Reply within 24 hours"],
    search: "Search by a word — for example \"labs\" or \"subscription\"",
    empty: "No questions match that search.",
    none: "No questions are available right now.",
    cta_title: "Didn't find your answer?",
    cta_p: "Send us your question — a person from the team replies, not an autoresponder.",
    cta_btn: "Ask a question",
    guide_btn: "Open the guide",
    back: "Home",
  },
  es: {
    eyebrow: "Centro de ayuda",
    title: "Las preguntas que más nos hacen",
    subtitle:
      "Estas son las respuestas que damos con más frecuencia en soporte. Si su pregunta no está aquí, escríbanos: respondemos personalmente.",
    meta: ["Plataforma educativa", "No es un diagnóstico médico", "Respuesta en 24 horas"],
    search: "Busque por una palabra — por ejemplo «análisis» o «suscripción»",
    empty: "Ninguna pregunta coincide con esa búsqueda.",
    none: "No hay preguntas disponibles en este momento.",
    cta_title: "¿No encontró su respuesta?",
    cta_p: "Envíenos su pregunta: responde una persona del equipo, no un mensaje automático.",
    cta_btn: "Hacer una pregunta",
    guide_btn: "Abrir la guía",
    back: "Inicio",
  },
};

export default function FAQPage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState(0);
  const [query, setQuery] = useState("");
  const t = TX[language] || TX.bg;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/faq`, { params: { lang: language } })
      .then((res) => { if (!cancelled) setFaqs(res.data?.items || []); })
      .catch(() => { if (!cancelled) setFaqs([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [language]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return faqs;
    return faqs.filter((f) => `${f.q || ""} ${f.a || ""}`.toLowerCase().includes(q));
  }, [faqs, query]);

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(223,47%,7%)]" : "bg-[#F9F9F7]"}`} data-testid="faq-page">
      <SEO title={t.title} description={t.subtitle} path="/faq" />
      {faqs.length > 0 && (
        <PageStructuredData
          type="FAQPage"
          data={{ faqs: faqs.map((f) => ({ q: f.question || f.q, a: f.answer || f.a })) }}
        />
      )}
      <SubpageNav />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
        <div className="pt-8">
          <SubpageBackLink label={t.back} />
        </div>
        <SubpageHero
          eyebrow={t.eyebrow}
          title={t.title}
          subtitle={t.subtitle}
          meta={t.meta}
          icon={HelpCircle}
          accent="#0e5c60"
          testId="faq-hero"
        />

        {/* Търсене */}
        <div className="relative mb-8">
          <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? "text-slate-500" : "text-slate-400"}`} />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.search}
            data-testid="faq-search-input"
            className={`w-full h-12 pl-11 pr-4 rounded-full border text-sm outline-none transition-colors ${
              isDark
                ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)] text-white placeholder:text-slate-500 focus:border-[#0e5c60]"
                : "bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-[#0e5c60]"
            }`}
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12" data-testid="faq-loading">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#0e5c60" }} />
          </div>
        ) : filtered.length === 0 ? (
          <div className={`text-center py-12 text-sm ${isDark ? "text-slate-500" : "text-slate-400"}`} data-testid="faq-empty">
            {faqs.length === 0 ? t.none : t.empty}
          </div>
        ) : (
          <div className={`rounded-2xl border overflow-hidden ${isDark ? "border-[hsl(223,47%,18%)]" : "border-slate-200/80"}`}>
            {filtered.map((faq, idx) => {
              const open = openIndex === idx;
              return (
                <div
                  key={faq.id || `faq-${idx}`}
                  data-testid={`faq-item-${idx}`}
                  className={`border-b last:border-b-0 ${isDark ? "border-[hsl(223,47%,18%)] bg-[hsl(223,47%,10%)]" : "border-slate-200/80 bg-white"}`}
                >
                  <button
                    onClick={() => setOpenIndex(open ? null : idx)}
                    data-testid={`faq-toggle-${idx}`}
                    className="w-full flex items-start justify-between gap-4 p-5 text-left"
                  >
                    <span className={`text-sm sm:text-base font-medium leading-snug ${isDark ? "text-white" : "text-[#101828]"}`}>
                      {faq.q}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 mt-1 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                      style={{ color: open ? "#0e5c60" : undefined }}
                    />
                  </button>
                  {open && (
                    <div
                      className={`px-5 pb-5 text-sm leading-relaxed whitespace-pre-line ${isDark ? "text-slate-400" : "text-slate-600"}`}
                      data-testid={`faq-answer-${idx}`}
                    >
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <div
          className={`mt-12 p-6 sm:p-8 rounded-3xl border ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-200/80 shadow-sm"}`}
          data-testid="faq-cta"
        >
          <MessageCircle className="w-5 h-5 mb-3" style={{ color: "#0e5c60" }} />
          <h2 className={`text-xl font-semibold mb-2 ${isDark ? "text-white" : "text-[#101828]"}`}>{t.cta_title}</h2>
          <p className={`text-sm leading-relaxed mb-5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>{t.cta_p}</p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="faq-contact-cta"
              className="inline-flex items-center gap-2 px-5 h-11 rounded-full text-sm font-semibold text-white"
              style={{ background: "linear-gradient(135deg,#0e5c60,#287a2e)" }}
            >
              {t.cta_btn} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/guide"
              data-testid="faq-guide-cta"
              className={`inline-flex items-center gap-2 px-5 h-11 rounded-full text-sm font-semibold border ${
                isDark ? "border-white/15 text-slate-200 hover:bg-white/5" : "border-slate-200 text-slate-700 hover:bg-slate-50"
              }`}
            >
              {t.guide_btn}
            </Link>
          </div>
        </div>
      </div>
      <SubpageFooter />
    </div>
  );
}
